/**
 * @module context/useSettingState
 * @description ChartSettingsContext 的类型安全读写 —— 把 unknown→类型 的边界收敛到唯一入口
 *
 * 与 `useChartSettingsSelector` 同属 Context 消费层：前者是裸 selector，本模块在
 * Schema 约束下封装 draft/base 合并与默认值，供 settingGroups 直接使用。
 *
 * 消费方不再写 `(s.draft.xxx ?? s.base.xxx) as string) || ''`；
 * 默认值来自 manifest/settingsSchema。
 *
 * ## 消费层三件套（对齐架构检视 §1.3）
 *
 * - `useSettingState`：单标量 key 读 + 写
 * - `useSettingObjectState`：单结构化 key 读 + 整对象/数组写
 * - `usePatchDraft`：多字段一次 UPDATE_DRAFT（无复杂 effect 时）
 * - 多字段 + effect/协议转换 → 功能 Hook（内部组合上述 API，UI 禁止裸 dispatch）
 */

import { useCallback } from 'react';
import {
  useChartSettingsDispatch,
  useChartSettingsSelector,
} from './ChartSettingsContext';
import { CSActionType, type TChartSettingsConfigLayer } from '../types';
import {
  SCALAR_SETTING_DEFAULTS,
  STRUCTURED_SETTING_DEFAULTS,
  type IScalarSettingsSchema,
  type IStructuredSettingsSchema,
  type TScalarSettingKey,
  type TStructuredSettingKey,
} from '../manifest/settingsSchema';

function readScalarSetting<K extends TScalarSettingKey>(
  draft: Record<string, unknown>,
  base: Record<string, unknown>,
  key: K,
): IScalarSettingsSchema[K] {
  const defaultValue = SCALAR_SETTING_DEFAULTS[key];

  if (key in draft) {
    const fromDraft = draft[key];
    if (typeof fromDraft === typeof defaultValue) {
      return fromDraft as IScalarSettingsSchema[K];
    }
    return defaultValue;
  }

  const fromBase = base[key];
  if (typeof fromBase === typeof defaultValue) {
    return fromBase as IScalarSettingsSchema[K];
  }

  return defaultValue;
}

export interface IUseSettingStateResult<K extends TScalarSettingKey> {
  value: IScalarSettingsSchema[K];
  setValue: (next: IScalarSettingsSchema[K]) => void;
}

/**
 * 读取并更新单个标量配置项（draft 优先，否则 base，否则 Schema 默认值）
 */
export function useSettingState<K extends TScalarSettingKey>(
  key: K,
): IUseSettingStateResult<K> {
  const dispatch = useChartSettingsDispatch();

  const value = useChartSettingsSelector(s =>
    readScalarSetting(s.draft, s.base, key),
  );

  const setValue = useCallback(
    (next: IScalarSettingsSchema[K]) => {
      dispatch({
        type: CSActionType.UPDATE_DRAFT,
        payload: { [key]: next },
      });
    },
    [dispatch, key],
  );

  return { value, setValue };
}

/**
 * 校验存储值与默认值「同形」（都为数组，或都为非空对象）
 *
 * 默认值的形状本身即判定依据：数组默认值 → 仅接受数组；对象默认值 → 仅接受非数组对象。
 */
function isStructuredMatch(value: unknown, fallback: unknown): boolean {
  if (Array.isArray(fallback)) {
    return Array.isArray(value);
  }
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

/**
 * 读取结构化配置项 —— 原样返回存储引用（不复制），缺省返回冻结默认引用
 *
 * 语义对齐旧 `selectSettingsDraftOrBaseRecord/Array`：draft 有效则取 draft，
 * 否则 base 有效则取 base，否则默认值。返回引用稳定，满足 selector Object.is 比较。
 */
/** 结构化字段读取（draft 优先 → base → Schema 冻结默认引用） */
export function readStructuredSetting<K extends TStructuredSettingKey>(
  draft: Record<string, unknown>,
  base: Record<string, unknown>,
  key: K,
): IStructuredSettingsSchema[K] {
  const fallback = STRUCTURED_SETTING_DEFAULTS[key];

  const fromDraft = draft[key];
  if (isStructuredMatch(fromDraft, fallback)) {
    return fromDraft as IStructuredSettingsSchema[K];
  }

  const fromBase = base[key];
  if (isStructuredMatch(fromBase, fallback)) {
    return fromBase as IStructuredSettingsSchema[K];
  }

  return fallback;
}

/**
 * 数组结构化字段的「draft-key」读法 —— 与 `readStructuredSetting` / `useSettingObjectState` 不同
 *
 * ## 为何不用 `useSettingObjectState`
 *
 * `useSettingObjectState` 内部固定走 `readStructuredSetting`：draft 上的值若不是合法数组，
 * 会**跳过 draft 继续读 base**。排序类字段在 draft 里常出现「key 仍在、值为 undefined/脏数据」
 * 或「显式 `[]` 表示用户清空」；此时不应再拾取 base 里上次 Confirm 的旧数组。
 *
 * ## 本函数规则
 *
 * - `'key' in draft` 为 true：只认 draft；非数组 → 返回 Schema 冻结空数组（不读 base）。
 * - key 不在 draft（例如 `UPDATE_DRAFT` 传 `undefined` 会 delete 该 key）：与 `readStructuredSetting`
 *   相同，可回退 base → 再默认。
 *
 * 用于 `xAxisValues`、`series_order`、`series_sorts` 等；双字段合并（如 chartSorts +
 * customMetricSort）仍用各自的 `resolveMetricSortFromState`。
 *
 * 语义对齐旧 `selectSettingsDraftOrBaseArrayRespectingDraftKey`。
 */
export function readStructuredArrayRespectingDraftKey<
  K extends TStructuredSettingKey,
>(
  draft: Record<string, unknown>,
  base: Record<string, unknown>,
  key: K,
): IStructuredSettingsSchema[K] {
  const fallback = STRUCTURED_SETTING_DEFAULTS[key];

  if (key in draft) {
    const fromDraft = draft[key];
    if (isStructuredMatch(fromDraft, fallback)) {
      return fromDraft as IStructuredSettingsSchema[K];
    }
    return fallback;
  }

  return readStructuredSetting(draft, base, key);
}

export interface IUseSettingObjectStateResult<K extends TStructuredSettingKey> {
  value: IStructuredSettingsSchema[K];
  setValue: (next: IStructuredSettingsSchema[K]) => void;
}

/**
 * 读取并更新单个结构化（对象/数组）配置项
 *
 * 与 `useSettingState` 的差异：返回值是「存储引用 / 冻结默认引用」，
 * 引用在内容未变时保持稳定；写入时整体替换该 key。
 */
export function useSettingObjectState<K extends TStructuredSettingKey>(
  key: K,
): IUseSettingObjectStateResult<K> {
  const dispatch = useChartSettingsDispatch();

  const value = useChartSettingsSelector(s =>
    readStructuredSetting(s.draft, s.base, key),
  );

  const setValue = useCallback(
    (next: IStructuredSettingsSchema[K]) => {
      dispatch({
        type: CSActionType.UPDATE_DRAFT,
        payload: { [key]: next },
      });
    },
    [dispatch, key],
  );

  return { value, setValue };
}

/**
 * 类型安全地合并写入 draft（多字段浅 patch）
 *
 * 用于同一交互需同时改多个 key、但尚不值得抽功能 Hook 的场景。
 */
export function usePatchDraft(): (
  patch: Partial<TChartSettingsConfigLayer>,
) => void {
  const dispatch = useChartSettingsDispatch();

  return useCallback(
    (patch: Partial<TChartSettingsConfigLayer>) => {
      dispatch({
        type: CSActionType.UPDATE_DRAFT,
        payload: patch,
      });
    },
    [dispatch],
  );
}
